// TypeScript interfaces for the Newsletter Redlink editor.
//
// Mirrors the backend contract served at /api/newsletter-redlinks (see
// node-app/src/routes/newsletter-redlinks.ts). Pure types only — the scenes
// call the API via the shared `fetchData` helper in helpers/apiUtils.ts, the
// same convention the PLX-mailing scene uses.

export type RedlinkStatus = 'draft' | 'active' | 'paused' | 'archived';
/**
 * Spiegelt `RedlinkTargetType` aus
 * node-app/src/services/NewsletterRedlinkService.ts. Bestimmt den
 * Tracking-Parameter: plx -> banner=, webinar -> affiliate=, livestream -> rdl=.
 */
export type RedlinkTargetType = 'plx' | 'webinar' | 'livestream';

/** Feste Ziel-URL jedes Livestream-Redlinks — serverseitig gesetzt, hier nur zur Anzeige. */
export const LIVESTREAM_LANDING_URL = 'https://live.finanztrends.de/';

/** Der Platzhalter, den der Text dieses Zieltyps tragen muss. */
export function redlinkLinkToken(t: RedlinkTargetType): string {
  switch (t) {
    case 'plx':
      return '{PRODUCT_LINK}';
    case 'webinar':
      return '{WEBINAR_LINK}';
    case 'livestream':
      return '{LIVESTREAM_LINK}';
  }
}

/** Alle Platzhalter — muss zu REDLINK_LINK_TOKEN_RE im Backend passen. */
export const REDLINK_LINK_TOKEN_RE = /\{PRODUCT_LINK\}|\{WEBINAR_LINK\}|\{LIVESTREAM_LINK\}/;

/**
 * Dynamische Datums-Platzhalter. Bewusst OHNE /g — mit dem Flag ist `.test()` zustandsbehaftet
 * (`lastIndex`) und liefert bei jedem zweiten Aufruf false. Spiegelt
 * `REDLINK_DATE_TOKEN_RE` in node-app/src/services/NewsletterRedlinkService.ts.
 */
export const REDLINK_DATE_TOKEN_RE = /\{WEBINAR_DATE_DYNAMIC(_UPPER)?\}/;

/** Anzeige-Etikett je Zieltyp (Chip, Filter, Detaildialog). */
export const REDLINK_TARGET_LABEL: Record<RedlinkTargetType, string> = {
  plx: 'PLX',
  webinar: 'Webinar',
  livestream: 'Live-Sendung',
};

export interface Redlink {
  id: number;
  target_type: RedlinkTargetType;
  product_id: number | null; // plx: Pflicht · livestream: optional (nur Beschriftung) · webinar: null
  webinar_id: string | null; // set iff target_type === 'webinar'
  headline: string;
  body_html: string;
  link_text: string; // CTA anchor label
  landing_url: string;
  concept: string; // 1-10 chars, uppercase, no spaces (banner attribution token)
  wkz_code: string | null;
  author_name: string | null; // byline "Gastbeitrag von {name}"; null = no byline
  author_image_url: string | null; // uploaded author photo (BunnyCDN)
  status: RedlinkStatus;
  scheduled_date: string | null; // YYYY-MM-DD when pinned to a date (active)
  created_by: string | null;
  generated_by: string | null; // 'ai' = autogen row, null = copywriter
  ai_kind: string | null; // 'heatup' (vor dem Webinar) | 'followup' (danach)
  ai_touch: number | null; // followup: 1 = Nachbericht, 2 = letzte Chance; heatup: nur Altbestand vor 2026-09-15
  ai_days_before: number | null; // heatup: Tage bis zum Webinar (0 = Webinar-Tag … 3)
  created_at: string;
  updated_at: string;
}

// GET /products → plx_products rows (id, plx_product, name, description)
export interface RedlinkProduct {
  id: number;
  plx_product: number;
  name: string;
  description: string;
}

// GET /products/:id/wkz → werbeaktion configs for the product
export interface RedlinkWkz {
  id: string;
  wkz_code: string;
  wkz_name: string;
  product_id: number;
  plx_product: number;
  active: number;
}

// GET /webinars → upcoming, signup-able, non-evergreen webinars
export interface RedlinkWebinar {
  webinar_id: string;
  title: string;
  signup_url: string;
  webinar_date: string;
  /** Zentraler Bewerbungsstatus (helpers/webinarPromotionStatus.ts) — nur Anzeige, kein Filter. */
  promo_status?: 'bewerbbar' | 'unvollstaendig' | 'deaktiviert' | 'vorbei';
  promo_label?: string;
  promo_hindernisse?: Array<{ code: string; text: string; ziel: 'lp' | 'reminder' | 'webinar' }>;
}

// GET /livestreams → geplante Live-Sendungen für den Datums-Picker.
// Enthält bewusst auch `pending`-Sendungen: eine Sendung wird erst am Sendetag
// bestätigt, ein Confirmed-Filter lieferte hier also fast immer nichts. Die
// Bestätigung erzwingt das Sendungs-Gate zur Sendezeit.
export interface RedlinkLivestream {
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  label: string | null;
  status: 'pending' | 'confirmed' | 'cancelled';
  host_email: string | null;
}

// GET /authors → ft_authors + remembered custom authors, each with its saved photo
export interface RedlinkAuthor {
  name: string;
  image_url: string | null;
}

// ─────────────────────── Evaluation (Part 2c) ───────────────────────
// GET /evaluation/redlinks, /evaluation/summary, /:id/evaluation.
// Reach (recipients/opens) = the newsletter's own Iterable campaign (the redlink
// ships to the whole audience). redlinkClicks = clicks on the redlink's own link
// (events cache); newsletterClicks = newsletter-wide (all links) — NOT redlink-specific.
// CPA-Umsatz = orders × anfo_cpa; cpaEtkp = cpaRevenue / recipients × 1000.

// GET /evaluation/redlinks → one row per (sent) redlink
export interface RedlinkEvaluation {
  id: number;
  headline: string;
  concept: string;
  targetType: RedlinkTargetType;
  status: RedlinkStatus;
  productName: string | null;
  webinarTitle: string | null;
  sendCount: number;
  firstSentAt: string | null;
  lastSentAt: string | null;
  totalRecipients: number;
  totalOpens: number;
  avgOpenRate: number; // percent
  redlinkClicks: number;
  redlinkCtr: number; // percent
  newsletterClicks: number; // newsletter-wide, not redlink-specific
  totalRegistrations: number; // webinar only
  totalAnfos: number;
  totalCpaRevenue: number;
  totalRevenue: number; // real zahlung_brutto, ~0 during trial
  cpaPerAnfo: number;
  cpaPerRegister: number; // webinar
  cpaEtkp: number;
  /**
   * Klicks auf eine CTA der Live-Seite (nur targetType 'livestream').
   * `null` = NICHT ERHOBEN — entweder kein Livestream-Redlink, oder die lokale
   * DB mit `live_cta_clicks` hat nicht geantwortet. Im UI als „–" rendern,
   * niemals als 0: „nicht gemessen" und „wirkungslos" sind verschiedene Aussagen.
   */
  ctaClicks: number | null;
}

export interface RedlinkEvaluationSummary {
  targetType: 'all' | RedlinkTargetType;
  redlinkCount: number;
  sendCount: number;
  totalRecipients: number;
  totalOpens: number;
  avgOpenRate: number;
  redlinkClicks: number;
  redlinkCtr: number;
  newsletterClicks: number;
  totalRegistrations: number;
  totalAnfos: number;
  totalCpaRevenue: number;
  totalRevenue: number;
  cpaPerAnfo: number;
  cpaPerRegister: number;
  cpaEtkp: number;
  /**
   * Klicks auf eine CTA der Live-Seite (nur targetType 'livestream').
   * `null` = NICHT ERHOBEN — entweder kein Livestream-Redlink, oder die lokale
   * DB mit `live_cta_clicks` hat nicht geantwortet. Im UI als „–" rendern,
   * niemals als 0: „nicht gemessen" und „wirkungslos" sind verschiedene Aussagen.
   */
  ctaClicks: number | null;
}

// GET /evaluation/redlinks response
export interface RedlinkEvaluationResponse {
  redlinks: RedlinkEvaluation[];
  summary: RedlinkEvaluationSummary;
  byTargetType: RedlinkEvaluationSummary[];
}

// One point in the per-send-day timeline
export interface RedlinkDailyPoint {
  sendDate: string;
  redlinkId: number;
  concept: string;
  targetType: RedlinkTargetType;
  recipients: number;
  opens: number;
  openRate: number;
  redlinkClicks: number;
  redlinkCtr: number;
  anfos: number;
  registrations: number;
  cpaRevenue: number;
  revenue: number;
  cpaEtkp: number;
  /**
   * Klicks auf eine CTA der Live-Seite (nur targetType 'livestream').
   * `null` = NICHT ERHOBEN — entweder kein Livestream-Redlink, oder die lokale
   * DB mit `live_cta_clicks` hat nicht geantwortet. Im UI als „–" rendern,
   * niemals als 0: „nicht gemessen" und „wirkungslos" sind verschiedene Aussagen.
   */
  ctaClicks: number | null;
}

// GET /evaluation/summary response
export interface RedlinkTimelineResponse {
  summary: RedlinkEvaluationSummary;
  byTargetType: RedlinkEvaluationSummary[];
  daily: RedlinkDailyPoint[];
}

// One send-day row in a redlink's drill-down
export interface RedlinkSendDay {
  sendDate: string;
  itCampaignId: number | null;
  recipients: number;
  opens: number;
  openRate: number;
  redlinkClicks: number;
  redlinkCtr: number;
  newsletterClicks: number;
  anfos: number;
  registrations: number;
  cpaRevenue: number;
  revenue: number;
  cpaPerAnfo: number;
  cpaEtkp: number;
  /**
   * Klicks auf eine CTA der Live-Seite (nur targetType 'livestream').
   * `null` = NICHT ERHOBEN — entweder kein Livestream-Redlink, oder die lokale
   * DB mit `live_cta_clicks` hat nicht geantwortet. Im UI als „–" rendern,
   * niemals als 0: „nicht gemessen" und „wirkungslos" sind verschiedene Aussagen.
   */
  ctaClicks: number | null;
}

// GET /:id/evaluation response
export interface RedlinkDetailResponse {
  redlink: {
    id: number;
    headline: string;
    concept: string;
    targetType: RedlinkTargetType;
    status: RedlinkStatus;
    productName: string | null;
    webinarTitle: string | null;
    landingUrl: string;
  };
  sends: RedlinkSendDay[];
  totals: RedlinkEvaluationSummary;
}
